import { Component, OnInit, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { ProfileService } from '../../../common/service/data/profile.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { getMonthName } from '../../../core/helper/month';
import { Command, CommandBase, FilteredCommand, standardCommand } from '../../../core/model/command';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteCommandHandler } from '../../../core/service/command/delete-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { EmailDeliveryStatus, EmailDeliveryStatusToString } from '../../../message/model/const/enums';
import { CustomPropertyVisibilityType, InvoicePeriod, InvoicePeriodToString, TenantCategory } from '../../../rent/model/const/enums';
import { invoiceCommand } from '../../command/unpaid-invoice';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethodToString, InvoiceType, InvoiceTypeToString } from '../../model/const/enum';
import { InvoiceCategory } from '../../model/invoice';
import { UnpaidInvoiceCommandHandlerResolver } from '../../service/command/unpaid-invoice-command-handler-resolver.service';
import { InvoiceService } from '../../service/data/invoice.service';
import { InvoiceListComponentBase } from '../base/invoice-list.base';
import { InvoiceListService } from './../../service/data/invoice-list.service';
import { CustomPropertyService } from '../../../rent/service/data/custom-property.service';
import { CustomPropertyHelper } from '../../../rent/helper/customproperties-helper';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { ContactService } from '../../../rent/service/data/contact.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { BasicOwnerConfig } from '../../../common/model/basic-owner-config';
import { LanguageService } from '../../../core/service/language.service';

@Component({
  selector: 'app-all-invoices',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
  providers: [DeleteCommandHandler, UnpaidInvoiceCommandHandlerResolver, InvoiceListService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InvoiceListComponent extends InvoiceListComponentBase implements OnInit {
  @ViewChild('createAmortizationPlanSidebar', { static: false }) createAmortizationPlanSidebar: TemplateRef<any>;

  amortizationPlanInvoiceIds: string[] = [];

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataService: InvoiceService,
    private readonly _profileService: ProfileService,
    private readonly _languageService: LanguageService,
    accountService: UserAccountService,
    dispatcher: CommandDispatcher,
    factory: ColumnDefinitionFactory,
    handlerResolver: UnpaidInvoiceCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private readonly _sidebarService: SidebarService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _invoiceListService: InvoiceListService,
    private readonly _companyService: CompanyService,
    private readonly _propertyService: PropertyService,
    private readonly _contactService: ContactService,
    private readonly _articleService: ArticleService
  ) {
    super(factory, accountService, _dataService, dispatcher, handlerResolver, messageService, navigationService, _invoiceListService);

    this.AddSubscription(
      accountService.current.subscribe({
        next: account => {
          this.allowNew = account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
          if (this.allowNew) {
            this.addContextMenuItemDefinition('INVOICE.COMMAND.CREATE_AMORTIZATION_PLAN', invoiceCommand.createAmortizationPlan);
            this.addMenuItemDefinition('INVOICE.COMMAND.CREATE_AMORTIZATION_PLAN', invoiceCommand.createAmortizationPlan);
          }
        },
      })
    );
    this.preferencesKey = 'invoice.list';
    this.columnDefinition = this._getColumnDefinitionGroup();
    this.showExcelExportButton = true;
    this.excelFileName = 'Fakturor';
    this.excelContext = 'CustomerLedgerInvoices';
    this.createExcelColumnDefinitions(factory);
  }

  private createExcelColumnDefinitions(factory: ColumnDefinitionFactory) {
    this.excelColumnDefinition = [...this.columnDefinition];
    this.excelColumnDefinition.push(
      factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId', ColumnVisibility.optional),
      factory.create('LANDLORD.ORGANIZATION_NUMBER', 'organizationNumber', ColumnVisibility.optional),
      factory.create('COMMON.INVOICE_ADDRESS', 'invoiceAddress', ColumnVisibility.optional),
      factory.create('INVOICE.PREVIEW.ROUNDING', 'rounding', ColumnVisibility.optional),
      factory.create('COMMON.CREDITOR', 'creditor', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_EMAIL', 'email', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber', ColumnVisibility.optional),
      factory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE', 'debtor1', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO', 'debtor2', ColumnVisibility.optional),
      factory.create('INVOICE.RECIPIENT_IDENTITY', 'invoiceRecipientIdentity', ColumnVisibility.optional),
      factory.create('INVOICE.RECIPIENT_PHONE', 'invoiceRecipientPhone', ColumnVisibility.optional),
      factory.create('INVOICE.REGULATED_TO', 'regulatedTo', ColumnVisibility.optional),
      factory.create('INVOICE.RECONCILIATION_UNPAID', 'reconciliationUnpaid', ColumnVisibility.optional),
      factory.create('JOINT_DEBITING_LIST.RENTAL_OBJECT_ADDRESS_COLUMN', 'rentalObjectAddress', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE_IDENTITY', 'debtor1Identity', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE_PHONE', 'debtor1Phone', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO_IDENTITY', 'debtor2Identity', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO_PHONE', 'debtor2Phone', ColumnVisibility.optional),
      factory.create('COMMON.GUARANTOR', 'guarantor', ColumnVisibility.optional),
      factory.create('INVOICE.GUARANTOR_IDENTITY', 'guarantorIdentity', ColumnVisibility.optional),
      factory.create('INVOICE.GUARANTOR_PHONE', 'guarantorPhone', ColumnVisibility.optional)
    );
  }

  private _category: InvoiceCategory;
  private _emailDeliveryStatus: EmailDeliveryStatus;

  override canExecute(command: CommandBase): boolean {
    if (command.id === invoiceCommand.createAmortizationPlan) {
      if (command instanceof Command) {
        return !command.contextId || command.contextId === this.contextId;
      }

      if (command instanceof FilteredCommand) {
        return command.totalAffectedItemsControlValue > 0;
      }

      return false;
    }

    return super.canExecute(command);
  }

  override execute(command: CommandBase): void {
    if (command.id === invoiceCommand.createAmortizationPlan) {
      if (command instanceof Command && command.context?.id) {
        this.openCreateAmortizationPlanSidebar([command.context.id]);
      } else if (command instanceof FilteredCommand) {
        if (command.includedIds?.length > 0) {
          this.openCreateAmortizationPlanSidebar(command.includedIds);
        } else {
          this.messageService.showMessage('AMORTIZATION_PLAN.CREATE.FILTERED_SELECTION_NOT_SUPPORTED');
        }
      }

      return;
    }

    super.execute(command);
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  ngOnInit() {
    this.AddSubscription(
      this._activatedRoute.queryParamMap.subscribe(map => {
        if (map.has('c')) {
          const param = map.get('c');
          this._category = InvoiceCategory[param[0].toUpperCase() + param.substring(1)];
        }

        if (map.has('d')) {
          const param = map.get('d');
          this._emailDeliveryStatus = EmailDeliveryStatus[param[0].toUpperCase() + param.substring(1)];
        }
      })
    );

    const externalQuery = this._category || this._emailDeliveryStatus;

    this.AddSubscription(
      this._customPropertyService.getList().subscribe(customProperties => {
        this._invoiceListService.getFilterDefinition().subscribe(definition => {
          this.defaultFilters = definition.defaultFields;
          const invPeriodsFrom = definition.invoicePeriods.map(x => {
            return {
              caption: `${x.month ? this._languageService.instant(getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
              value: x.toId(),
            };
          });
          invPeriodsFrom.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: '' });

          const invPeriodsTo = definition.invoicePeriods.map(x => ({
            caption: `${x.month ? this._languageService.instant(getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: x.toId(),
          }));
          invPeriodsTo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: '' });

          const yesNo = [
            { caption: 'YES_TEXT', value: 1 },
            { caption: 'NO_TEXT', value: 2 },
          ];
          yesNo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });

          const selectedCompanies = definition.company
            ? Array.isArray(definition.company)
              ? definition.company
              : [definition.company]
            : [];

          const selectedArticles = definition.articles
            ? Array.isArray(definition.articles)
              ? definition.articles
              : [definition.articles]
            : [];

          let category = InvoiceCategory.Any;
          if (this._category) {
            category = this._category;
          } else if (!externalQuery && definition.category) category = definition.category;

          let emailStatus = 0;
          if (this._emailDeliveryStatus) {
            emailStatus = this._emailDeliveryStatus;
          } else if (!externalQuery && definition.emlDeliveryStatusKind) emailStatus = definition.emlDeliveryStatusKind;

          const deliverySelection = [{ caption: 'INVOICE.CATEGORY.ANY', value: 0, icon: undefined }];

          for (const method of definition.invoiceDeliveryMethods) {
            deliverySelection.push({
              caption: InvoiceDeliveryMethodToString(method),
              value: method,
              icon: GetInvoiceDeliveryMethodIcon(method),
            });
          }

          const recipientCategorySelection = [
            { caption: 'INVOICE.CATEGORY.ANY', value: null },
            { caption: 'CONST.TENANT_CATEGORY_PHYSICAL_PERSON', value: TenantCategory.PhysicalPerson },
            { caption: 'CONST.TENANT_CATEGORY_COMPANY', value: TenantCategory.Company },
          ];

          const invoiceTypeSelection = [
            { caption: 'INVOICE.CATEGORY.ANY', value: null },
            { caption: InvoiceTypeToString(InvoiceType.Credit), value: InvoiceType.Credit },
            { caption: InvoiceTypeToString(InvoiceType.General), value: InvoiceType.General },
            { caption: InvoiceTypeToString(InvoiceType.Rent), value: InvoiceType.Rent },
          ];

          const invoiceNumberFilter = new FilterDefinitionItem(
            'INVOICE.INVOICE_NUMBER',
            'invoiceNumber',
            externalQuery ? '' : definition.invoiceNumber,
            null,
            definition.selectedFields.includes('invoiceNumber')
          );
          invoiceNumberFilter.placeholderText = 'INVOICE.INVOICE_NUMBER';

          this.filterDefinition = [
            new FilterDefinitionItem(
              'INVOICE.QUERY.CATEGORY',
              'category',
              category,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: InvoiceCategory.Any },
                { caption: 'INVOICE.CATEGORY.NOT_ATTESTED', value: InvoiceCategory.NotAttested },
                { caption: 'INVOICE.CATEGORY.ATTESTED', value: InvoiceCategory.Attested },
                { caption: 'INVOICE.CATEGORY.OVERDUE', value: InvoiceCategory.Overdue },
                { caption: 'INVOICE.CATEGORY.PARTIALLY_PAID', value: InvoiceCategory.PartiallyPaid },
                { caption: 'INVOICE.CATEGORY.UNPAID', value: InvoiceCategory.Unpaid },
                { caption: 'INVOICE.CATEGORY.FULLY_PAID', value: InvoiceCategory.FullyPaid },
              ],
              this._category ? true : definition.selectedFields.includes('category')
            ),
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'company',
              externalQuery ? [] : selectedCompanies,
              this._companyService,
              definition.selectedFields.includes('company'),
              true
            ),
            new FilterDefinitionItem(
              'ARTICLE.EDIT_HEADER',
              'articles',
              externalQuery ? [] : selectedArticles,
              this._articleService,
              definition.selectedFields.includes('articles'),
              true
            ),
            invoiceNumberFilter,
            new FilterDefinitionItem(
              'INVOICE.OCRNUMBER',
              'ocr',
              externalQuery ? '' : definition.ocr,
              null,
              definition.selectedFields.includes('ocr')
            ),
            new FilterDefinitionItem(
              'TENANT.EDIT_HEADER',
              'recipientContactId',
              externalQuery ? '' : definition.recipientContactId,
              this._contactService,
              definition.selectedFields.includes('recipientContactId')
            ),
            new FilterDefinitionItem(
              'INVOICE.PERIOD_FROM',
              'periodFrom',
              externalQuery ? '' : definition.periodFrom,
              invPeriodsFrom,
              definition.selectedFields.includes('periodFrom')
            ),
            new FilterDefinitionItem(
              'INVOICE.PERIOD_TO',
              'periodTo',
              externalQuery ? '' : definition.periodTo,
              invPeriodsTo,
              definition.selectedFields.includes('periodTo')
            ),
            new FilterDefinitionItemLocalDate(
              'INVOICE.CREATED',
              'invoiceDate',
              externalQuery ? null : definition.invoiceDate,
              definition.selectedFields.includes('invoiceDate')
            ),
            new FilterDefinitionItemLocalDate(
              'INVOICE.DUE_DATE',
              'dueDate',
              externalQuery ? null : definition.dueDate,
              definition.selectedFields.includes('dueDate')
            ),
            new FilterDefinitionItem(
              'INVOICE.DELIVERY_METHOD',
              'invDeliveryMethod',
              externalQuery ? 0 : definition.invDeliveryMethod,
              deliverySelection,
              definition.selectedFields.includes('invDeliveryMethod')
            ),
            new FilterDefinitionItem(
              'COMMON.EMAIL_DELIVERY_STATUS_EMAIL',
              'emlDeliveryStatusKind',
              emailStatus,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: 0 },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Processed), value: EmailDeliveryStatus.Processed },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Dropped), value: EmailDeliveryStatus.Dropped },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Deferred), value: EmailDeliveryStatus.Deferred },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Delivered), value: EmailDeliveryStatus.Delivered },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Open), value: EmailDeliveryStatus.Open },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.SpamReport), value: EmailDeliveryStatus.SpamReport },
                {
                  caption: EmailDeliveryStatusToString(EmailDeliveryStatus.NotDeliveredLastWeek),
                  value: EmailDeliveryStatus.NotDeliveredLastWeek,
                },
              ],
              definition.selectedFields.includes('emlDeliveryStatusKind')
            ),
            new FilterDefinitionItem(
              'LEASE_AGREEMENT.INVOICE_PERIOD',
              'invPeriodMonths',
              externalQuery ? 0 : definition.invPeriodMonths,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: 0 },
                { caption: InvoicePeriodToString(InvoicePeriod.Month), value: InvoicePeriod.Month },
                { caption: InvoicePeriodToString(InvoicePeriod.TwoMonths), value: InvoicePeriod.TwoMonths },
                { caption: InvoicePeriodToString(InvoicePeriod.Quarter), value: InvoicePeriod.Quarter },
                { caption: InvoicePeriodToString(InvoicePeriod.Tertrial), value: InvoicePeriod.Tertrial },
                { caption: InvoicePeriodToString(InvoicePeriod.HalfYear), value: InvoicePeriod.HalfYear },
                { caption: InvoicePeriodToString(InvoicePeriod.Year), value: InvoicePeriod.Year },
              ],
              definition.selectedFields.includes('invPeriodMonths')
            ),

            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
              'property',
              externalQuery ? '' : definition.property,
              this._propertyService,
              definition.selectedFields.includes('property')
            ),
            new FilterDefinitionItem(
              'INVOICE.DIRECT_DEBIT_INVOICE',
              'directDebit',
              externalQuery ? 0 : definition.directDebit,
              yesNo,
              definition.selectedFields.includes('directDebit')
            ),
            new FilterDefinitionItem(
              'INVOICE.VAT_INVOICE',
              'vatInvoice',
              externalQuery ? 0 : definition.vatInvoice,
              yesNo,
              definition.selectedFields.includes('vatInvoice')
            ),
            new FilterDefinitionItem(
              'LEASE_AGREEMENT.LABELING',
              'labeling',
              externalQuery ? '' : definition.labeling,
              null,
              definition.selectedFields.includes('labeling')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
              'rentalId',
              externalQuery ? '' : definition.rentalId,
              null,
              definition.selectedFields.includes('rentalId')
            ),
            new FilterDefinitionItem(
              'INVOICE.DELIVERY_METHOD_EXECUTED',
              'invDeliveryMethodExecuted',
              externalQuery ? 0 : definition.invDeliveryMethodExecuted,
              deliverySelection,
              definition.selectedFields.includes('invDeliveryMethodExecuted')
            ),
            new FilterDefinitionItem(
              'INVOICE.RECIPIENT_CATEGORY',
              'recipientCategory',
              externalQuery ? null : definition.recipientCategory,
              recipientCategorySelection,
              definition.selectedFields.includes('recipientCategory')
            ),
            new FilterDefinitionItem(
              'INVOICE.TYPE',
              'invoiceType',
              externalQuery ? null : definition.invoiceType,
              invoiceTypeSelection,
              definition.selectedFields.includes('invoiceType')
            ),
          ];
          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company
          );
          this._ch.createCustomPropertyColumnDefinition(customProperties, this.columnDefinition, CustomPropertyVisibilityType.Company);
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
        });
      })
    );

    if (this.allowNew) {
      this.addEditDeleteCommands();
    } else {
      this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
    }
  }

  closeAmortizationPlanSidebar(): void {
    this._sidebarService.toggleSidebar(false);
  }

  onAmortizationPlanCreated(): void {
    this.closeAmortizationPlanSidebar();
    this.reloadCurrentList();
  }

  private openCreateAmortizationPlanSidebar(invoiceIds: string[]): void {
    this.amortizationPlanInvoiceIds = (invoiceIds ?? []).filter(id => !!id);
    this._sidebarService.setSidebarContent({
      template: this.createAmortizationPlanSidebar,
      onClose: () => {
        this.amortizationPlanInvoiceIds = [];
      },
    });
    this._sidebarService.toggleSidebar(true);
  }

  private reloadCurrentList(): void {
    const selectedFields =
      this.filterComponent?.filters?.filter(filter => filter.selected).map(filter => filter.name) ?? this.filterState?.selectedFields;
    const filter = {
      ...(this.filterComponent?.form?.value ?? this.filterState ?? this.getPagingChangedFallbackFilter()),
      selectedFields: selectedFields ?? this.defaultFilters ?? [],
    };

    (this as any).sendPageFilter(filter);
  }
}
